import espritArbres from "@/assets/esprit-arbres-gemmo.png.asset.json";
import unaeAsset from "@/assets/unae-omega3.png.asset.json";
import nutripureAsset from "@/assets/partner-nutripure-product.png.asset.json";
import fenouxAsset from "@/assets/partner-fenioux-product.png.asset.json";
import sundayAsset from "@/assets/partner-sunday-product.png.asset.json";
import auriculoAsset from "@/assets/auriculotherapie.webp.asset.json";

export interface Article {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt?: string;
  partner?: {
    brandName: string;
    code?: string;
    shopUrl: string;
    description: string;
  };
  body: string[];
  bodyHtml?: string;
}

export const articles: Article[] = [
  {
    slug: "qu-est-ce-que-la-naturopathie",
    category: "Santé naturelle",
    title: "Qu'est-ce que la naturopathie ?",
    excerpt: "Définition, principes et bienfaits de la naturopathie — une approche globale de la santé qui s'appuie sur les capacités d'auto-guérison du corps.",
    date: "Juin 2026",
    readTime: "7 min",
    image: auriculoAsset.url,
    imageAlt: "Séance de naturopathie au cabinet Rénergescence à Toulouse",
    body: [
      "La naturopathie est une approche globale de la santé qui vise à préserver et restaurer l'équilibre de l'organisme par des moyens naturels.",
    ],
    bodyHtml: `
<h3>Définition de la naturopathie</h3>
<p>La <strong>naturopathie</strong> est une médecine traditionnelle reconnue par l'Organisation Mondiale de la Santé (OMS) comme la troisième médecine traditionnelle mondiale, aux côtés des médecines chinoise et ayurvédique. Elle repose sur l'idée que le corps possède une capacité naturelle d'auto-régulation et d'auto-guérison — le rôle du naturopathe est de la soutenir.</p>
<p>Plutôt que de traiter un symptôme isolé, la naturopathie cherche à comprendre <strong>l'origine d'un déséquilibre</strong> et à agir sur le terrain de la personne dans sa globalité : corps, émotions, mode de vie, environnement.</p>

<h3>Les grands principes</h3>
<ul>
  <li><strong>L'individualité</strong> — chaque personne est unique. Le bilan de vitalité permet de proposer un accompagnement réellement personnalisé.</li>
  <li><strong>Le terrain avant le symptôme</strong> — un trouble est rarement isolé ; il s'inscrit dans une histoire et un fonctionnement global.</li>
  <li><strong>L'éducation à la santé</strong> — le naturopathe transmet des clés concrètes pour devenir autonome dans son hygiène de vie.</li>
  <li><strong>La prévention</strong> — agir en amont, soutenir le terrain et préserver la vitalité avant que le déséquilibre ne s'installe.</li>
</ul>

<h3>Les outils du naturopathe</h3>
<p>La naturopathie s'appuie sur dix techniques principales, parmi lesquelles :</p>
<ul>
  <li><strong>L'alimentation</strong> — première médecine, adaptée au terrain et au mode de vie de chacun.</li>
  <li><strong>La phytothérapie et l'aromathérapie</strong> — usage des plantes et des huiles essentielles.</li>
  <li><strong>La micronutrition</strong> — soutien ciblé en vitamines, minéraux et oligo-éléments.</li>
  <li><strong>La gestion du stress et des émotions</strong> — respiration, relaxation, accompagnement global.</li>
  <li><strong>Les techniques manuelles et réflexes</strong> — massages bien-être, auriculothérapie.</li>
</ul>

<h3>À qui s'adresse la naturopathie ?</h3>
<p>La naturopathie s'adresse à toute personne souhaitant <strong>préserver ou retrouver un équilibre</strong> : fatigue chronique, troubles digestifs, déséquilibres hormonaux, sommeil perturbé, stress, baisse d'immunité, accompagnement de périodes de vie (grossesse, ménopause, transitions professionnelles)…</p>
<p>Elle vient en <strong>complément</strong> de la médecine conventionnelle, jamais en substitution. Le naturopathe ne pose pas de diagnostic et n'interrompt aucun traitement médical.</p>

<h3>Comment se déroule une consultation ?</h3>
<p>Une première séance dure environ 1h30. Elle commence par un <strong>bilan de vitalité</strong> approfondi : antécédents, mode de vie, alimentation, sommeil, gestion du stress, objectifs. À l'issue de l'échange, je propose un programme personnalisé combinant hygiène alimentaire, conseils en phytothérapie ou micronutrition, techniques de gestion du stress et parfois soin manuel.</p>
<p>Le suivi se construit ensuite sur plusieurs séances pour ajuster le programme et accompagner les changements dans la durée.</p>

<h3>Pour aller plus loin</h3>
<p>Pour découvrir mon approche en cabinet, consultez la page <a href="/prestations">prestations</a> ou <a href="/contact">prenez rendez-vous</a> directement. Le <a href="/tarifs">tarif des consultations</a> est disponible en ligne.</p>
`,
  },
  {
    slug: "nutripure-complements",
    category: "Partenaires",
    title: "Nutripure : les compléments que je recommande",
    excerpt: "Des compléments alimentaires premium, sans additifs inutiles, sélectionnés pour leur exigence de formulation.",
    date: "Mars 2024",
    readTime: "4 min",
    image: nutripureAsset.url,
    partner: {
      brandName: "Nutripure",
      code: "CAROBLC",
      shopUrl: "https://www.nutripure.fr",
      description: "Compléments alimentaires sans additifs inutiles, formulés en France.",
    },
    body: [
      "La recommandation de compléments alimentaires fait partie intégrante de mes accompagnements en santé naturelle. Ils permettent de soutenir, réguler et combler les carences lorsqu'elles sont présentes.",
      "Plutôt que de multiplier les références, je choisis de m'appuyer sur quelques marques que je connais bien et dont je peux retracer les choix de formulation.",
      "Nutripure fait partie de celles-ci. Leur approche est exigeante : ingrédients sourcés avec soin, dosages cohérents, absence d'excipients superflus. C'est cette transparence et cette efficacité qui me permet de recommander leurs produits en confiance lors d'un bilan.",
      "Le code partenaire ci-dessous vous donne accès à une remise sur l'ensemble de leur catalogue. Il reste évidemment optionnel : un complément ne se choisit jamais seul, mais toujours dans le cadre d'un suivi global.",
    ],
  },
  {
    slug: "laboratoires-fenioux",
    category: "Partenaires",
    title: "Les Laboratoires Fenioux : phytothérapie de qualité",
    excerpt: "Phytothérapie et micronutrition professionnelle, une référence solide pour accompagner les déséquilibres durables.",
    date: "Février 2024",
    readTime: "5 min",
    image: fenouxAsset.url,
    partner: {
      brandName: "Les Laboratoires Fenioux",
      code: "310309",
      shopUrl: "https://www.laboratoires-fenioux.com/fr",
      description: "Phytothérapie et micronutrition de qualité professionnelle.",
    },
    body: [
      "La phytothérapie reste l'un des piliers de l'accompagnement en naturopathie. Bien utilisée, elle permet de soutenir l'organisme sur des terrains précis — fatigue chronique, sphère hormonale, sommeil, immunité.",
      "Je travaille avec Les Laboratoires Fenioux depuis plusieurs années. Leurs gammes sont conçues avec rigueur, à partir de plantes traçables, et leur catalogue couvre la plupart des besoins rencontrés en cabinet.",
      "Le code partenaire est demandé lors de vos commandes et permet à mes consultants d'accéder à des conditions préférentielles, dans la continuité du programme établi en consultation.",
    ],
  },
  {
    slug: "esprit-des-arbres",
    category: "Partenaires",
    title: "L'esprit des arbres : la gemmothérapie au cabinet",
    excerpt: "La gemmothérapie expliquée — bourgeons et jeunes pousses macérés pour soutenir la vitalité, en partenariat avec Sabine Boistard.",
    date: "Janvier 2024",
    readTime: "6 min",
    image: espritArbres.url,
    imageAlt: "Flacon de macérat de bourgeons de framboisier — L'esprit des arbres, gemmothérapie artisanale",
    partner: {
      brandName: "L'esprit des arbres",
      shopUrl: "https://www.lespritdesarbres.com/",
      description: "Gemmothérapie artisanale — macérats de bourgeons en miel et cognac biodynamique.",
    },
    body: [
      "La gemmothérapie repose sur l'utilisation des bourgeons et jeunes pousses des arbres et arbustes pour maintenir ou restaurer la santé.",
    ],
    bodyHtml: `
<h3>La gemmothérapie, c'est quoi ?</h3>
<p>Il s'agit de l'utilisation des <strong>bourgeons et des jeunes pousses des arbres et des arbustes</strong> pour maintenir ou restaurer la santé. Ces bourgeons sont macérés dans un mélange d'alcool et de glycérine ou de miel.</p>
<p>Chaque bourgeon renferme le potentiel d'un arbre et permet d'apporter ses propriétés et activités dans le but de <strong>régénérer des organes affaiblis, drainer, équilibrer</strong> ou apporter une meilleure vitalité générale.</p>

<h3>Quand utiliser la gemmothérapie ?</h3>
<ul>
  <li>Affections chroniques nécessitant un travail de terrain</li>
  <li>Cures de jouvence et soutien de la vitalité</li>
  <li>Plaisir du goût, en complément d'un suivi en <a href="/prestations#naturopathie">naturopathie</a></li>
</ul>
<p>C'est une thérapeutique douce de terrain qui respecte les arbres et l'environnement : la cueillette est manuelle et seul un tiers des bourgeons est récolté afin de préserver le développement de l'arbre.</p>

<h3>Pourquoi je recommande L'esprit des arbres</h3>
<p>Lorsqu'il s'agit de gemmothérapie, je recommande toujours sans hésiter les produits locaux de <strong>Sabine Boistard</strong>, à commander directement sur son site ou par téléphone : <a href="https://www.lespritdesarbres.com/" target="_blank" rel="noopener noreferrer">lespritdesarbres.com</a>.</p>
<p>Ses produits sont excellents, tant sur la qualité que sur le goût.</p>

<h3>Des matières d'exception</h3>
<p><em>Extrait du site L'esprit des arbres :</em></p>
<ul>
  <li><strong>Bourgeons</strong> — issus de forêts éloignées de sources de pollutions directes, des forêts paix où la vie sauvage est développée. Les bourgeons et jeunes pousses qui ne proviennent pas de forêts sont issus de plantes de garrigues ou de leurs propres jardins.</li>
  <li><strong>Flacons</strong> — un verre violet « miron » aux qualités exceptionnelles pour la conservation des plantes, bien plus coûteux que les verres bruns ou bleutés. Après utilisation, les flacons peuvent être réutilisés pour stocker huiles essentielles, huiles de macération ou élixirs de plantes.</li>
  <li><strong>Alcool</strong> — un cognac issu d'une agriculture biodynamique. Sur une petite surface de vignes, David prend soin de ses cépages avec douceur et bienveillance — l'alcool idéal pour les préparations médicinales.</li>
  <li><strong>Miel</strong> — par choix, et après des années d'observation, la glycérine industrielle est remplacée par du miel d'acacia, récolté par Dany et Stéphanie près de Mirepoix, dont les ruches itinérantes migrent au gré des floraisons.</li>
</ul>

<h3>Pour aller plus loin</h3>
<p>La gemmothérapie s'intègre idéalement à un <a href="/prestations#naturopathie">accompagnement en santé naturelle</a>. Pour un protocole personnalisé, n'hésitez pas à <a href="/contact">prendre rendez-vous au cabinet</a>.</p>
`,
  },
  {
    slug: "unae-cosmetique",
    category: "Partenaires",
    title: "Unae : compléments alimentaires naturels & Oméga-3 MSC",
    excerpt: "Compléments alimentaires français, formulations claires et ingrédients soigneusement sélectionnés — dont l'Oméga-3 de poissons issu de pêche durable MSC.",
    date: "Décembre 2023",
    readTime: "4 min",
    image: unaeAsset.url,
    imageAlt: "Flacon Unae Oméga-3 de poissons MSC — complément alimentaire issu de pêche durable",
    partner: {
      brandName: "Unae",
      code: "CABRENE10",
      shopUrl: "https://www.unae.fr",
      description: "Compléments alimentaires français, formulations minimalistes.",
    },
    body: [
      "En micronutrition, la qualité des compléments fait toute la différence. Une formulation claire, un dosage juste, des ingrédients tracés : ce sont les critères que je recherche pour mes consultants.",
      "Unae propose une gamme de compléments alimentaires français, avec des formules courtes et une vraie transparence sur leurs ingrédients. Une marque que je recommande sans réserve dans le cadre d'un suivi naturopathique.",
    ],
  },
  {
    slug: "sunday-natural",
    category: "Partenaires",
    title: "Sunday Natural : l'exigence qualité en suppléments",
    excerpt: "Une marque allemande à l'exigence rare en matière de suppléments naturels et de transparence.",
    date: "Novembre 2023",
    readTime: "5 min",
    image: sundayAsset.url,
    partner: {
      brandName: "Sunday Natural",
      shopUrl: "https://www.sunday.fr/",
      description: "Suppléments naturels avec une exigence qualité remarquable.",
    },
    body: [
      "Quand il s'agit de micronutrition pointue — vitamine D, magnésium, oméga 3 — la qualité fait toute la différence. Sunday Natural se distingue par une exigence presque pharmaceutique sur ses matières premières.",
      "C'est une référence que j'utilise lorsque la précision du dosage et la pureté du produit comptent vraiment. Un investissement qui se justifie sur les protocoles longs.",
    ],
  },
];
